// This file deals with Marketplace API which will create Stripe Custom Connect accounts
// from given bank_account tokens.
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as log from '../util/log';
import { storableError } from '../util/errors';
import { getStripeAccountTokenInfo } from '../util/stripeConnect';
import { parseDateOfBirth } from '../util/dateOfBirth';

// ================ Async thunks ================ //

///////////////////////////
// Create Stripe Account //
///////////////////////////
// Pull the data we already have on the user (name + email) into the
// shape Stripe's account token expects so the seller doesn't re-type
// these fields on Stripe's hosted onboarding page. Phone + address
// aren't collected at signup yet — Stripe will prompt for those on
// its side. Returns the inner `individual: {...}` / `company: {...}`
// object based on the chosen account type, or `{}` if no current user.
export const getStripePrefillFromUser = (currentUser, accountType) => {
  if (!currentUser?.attributes) return {};
  const profile = currentUser.attributes.profile || {};
  const firstName = profile.firstName || undefined;
  const lastName = profile.lastName || undefined;
  const email = currentUser.attributes.email || undefined;
  // Phone is stored on the user's profile in canonical E.164 form
  // (`+47XXXXXXXX`) by FieldPhoneNumberInput's parse. Stripe wants
  // the same shape on `individual.phone` / `company.phone` so we can
  // pass it through unchanged — when present it satisfies the phone
  // requirement on Stripe-hosted onboarding and the seller doesn't
  // get asked for it again.
  //
  // Stripe is stricter than our own form validator about what counts
  // as a real Norwegian number: every digit after +47 must form a
  // recognisable mobile (4/9 prefix) or landline (2/3/5/6/7/8 prefix)
  // number. Test inputs like "+4712345678" (leading 1) get rejected
  // with a `"…" is not a valid phone number` error from
  // stripe.createToken. Skip the prefill silently when the value
  // doesn't pass that gate — the seller can still type it on
  // Stripe-hosted onboarding, and we don't block the entire create
  // call over a phone field.
  const rawPhone = profile.protectedData?.phoneNumber;
  const phone = /^\+47[2-9]\d{7}$/.test(rawPhone || '') ? rawPhone : undefined;

  // Prefill the seller's address from their saved sender address so
  // Stripe-hosted onboarding picks it up and they don't have to retype
  // it (same idea as the phone). Map our shape
  // ({ addressLine, addressLine2, postalCode, city }) onto Stripe's
  // structured `address` ({ line1, line2, postal_code, city, country }).
  // Only include it when the essentials are present — a partial address
  // is skipped silently so it can never block account creation; the
  // seller can still enter it on Stripe.
  const shippingAddress = profile.privateData?.shippingAddress || {};
  const address =
    shippingAddress.addressLine && shippingAddress.postalCode && shippingAddress.city
      ? {
          line1: shippingAddress.addressLine,
          ...(shippingAddress.addressLine2 ? { line2: shippingAddress.addressLine2 } : {}),
          postal_code: shippingAddress.postalCode,
          city: shippingAddress.city,
          country: 'NO',
        }
      : undefined;

  if (accountType !== 'individual') {
    // A company token takes the company's own name, phone and address, never
    // a person's names or email: Stripe rejects the whole token ("company.
    // first_name is not an accepted parameter") and no business could set up
    // payouts. The name and address come from the business fields given at
    // sign-up; the person behind the account is asked for on Stripe's page.
    const publicData = profile.publicData || {};
    const companyName = (publicData.companyName || '').trim() || undefined;
    const companyAddress =
      publicData.streetAddress && publicData.postalCode && publicData.city
        ? {
            line1: publicData.streetAddress,
            postal_code: publicData.postalCode,
            city: publicData.city,
            country: 'NO',
          }
        : address;
    const company = {
      ...(companyName ? { name: companyName } : {}),
      ...(phone ? { phone } : {}),
      ...(companyAddress ? { address: companyAddress } : {}),
    };
    return Object.keys(company).length > 0 ? { company } : {};
  }

  // Only include defined fields — Stripe rejects empty strings.
  const prefill = {
    ...(firstName ? { first_name: firstName } : {}),
    ...(lastName ? { last_name: lastName } : {}),
    ...(email ? { email } : {}),
    ...(phone ? { phone } : {}),
    ...(address ? { address } : {}),
  };

  return Object.keys(prefill).length > 0 ? { individual: prefill } : {};
};

const createStripeAccountPayloadCreator = (params, { extra: sdk, getState, rejectWithValue }) => {
  if (typeof window === 'undefined' || !window.Stripe) {
    throw new Error('Stripe must be loaded for submitting PayoutPreferences');
  }
  const {
    country,
    accountType,
    dateOfBirth,
    businessProfileMCC,
    businessProfileURL,
    stripePublishableKey,
  } = params;
  const stripe = window.Stripe(stripePublishableKey);

  // Capabilities are a collection of settings that can be requested for each provider.
  // What Capabilities are required determines what information Stripe requires to be
  // collected from the providers.
  // You can read more from here: https://stripe.com/docs/connect/capabilities-overview
  // Note: with default processes, both 'card_payments' and 'transfers' are required.
  const requestedCapabilities = ['card_payments', 'transfers'];

  const currentUser = getState()?.user?.currentUser;
  const userPrefill = getStripePrefillFromUser(currentUser, accountType);

  const accountInfo = {
    ...getStripeAccountTokenInfo({ country, accountType }),
    ...userPrefill,
    tos_shown_and_accepted: true,
  };

  // Date of birth typed on the payout form (individuals only). It rides
  // into the individual's identity on the client-side account token so
  // Stripe prefills it on the hosted page — never stored on our side.
  const dob = accountType === 'individual' ? parseDateOfBirth(dateOfBirth) : null;
  if (dob) {
    accountInfo.individual = { ...(accountInfo.individual || {}), dob };
  }

  // We deliberately do NOT send a bank account to Stripe at
  // account-creation time. If the bank account is prefilled (or entered)
  // during onboarding, Stripe forces the seller to *confirm* it by
  // typing the full IBAN on the hosted page — a dead end for Norwegian
  // users who only know their 11-digit kontonummer. Instead the account
  // is created identity-only, and the external account is attached via
  // the API after onboarding (see `attachExternalAccount`), so the IBAN
  // never leaves our side. This relies on "Collect external account
  // information" being disabled in the Stripe Dashboard (Connect >
  // payouts > onboarding); otherwise hosted onboarding still asks the
  // seller for the bank account.

  // stripe.createToken rejects with { error } rather than throwing on
  // a 400. Surface its `error.message` so the form's `stripeAccountError`
  // path renders Stripe's own message instead of crashing on
  // `undefined.token.id`.
  const assertStripeToken = response => {
    if (response?.error) {
      const stripeErr = new Error(response.error.message || 'Stripe token creation failed');
      stripeErr.stripeError = response.error;
      throw stripeErr;
    }
    return response.token.id;
  };

  return stripe
    .createToken('account', accountInfo)
    .then(accountTokenResponse => {
      const accountToken = assertStripeToken(accountTokenResponse);
      return sdk.stripeAccount.create(
        {
          country,
          accountToken,
          requestedCapabilities,
          businessProfileMCC,
          businessProfileURL,
        },
        { expand: true }
      );
    })
    .then(response => {
      const stripeAccount = response.data.data;
      return stripeAccount;
    })
    .catch(err => {
      const e = storableError(err);
      // For Stripe.js token errors (thrown by assertStripeToken above)
      // graft the stripeError.message into the storable error's
      // apiErrors so the form's existing ErrorsMaybe display picks it
      // up the same way it picks up Stripe errors wrapped by
      // Sharetribe's `stripe_account/create` API.
      if (err?.stripeError && !(e.apiErrors && e.apiErrors.length)) {
        e.apiErrors = [
          {
            status: 400,
            code: err.stripeError.code || 'stripe-token-create-failed',
            meta: { stripeMessage: err.stripeError.message },
          },
        ];
      }
      const stripeMessage =
        e.apiErrors && e.apiErrors.length > 0 && e.apiErrors[0].meta
          ? e.apiErrors[0].meta.stripeMessage
          : null;
      log.error(err, 'create-stripe-account-failed', { stripeMessage });
      return rejectWithValue(e);
    });
};
export const createStripeAccountThunk = createAsyncThunk(
  'stripeConnectAccount/createStripeAccount',
  createStripeAccountPayloadCreator
);
// The Stripe-setup nudge (server/api/admin/nudges/stripe-setup.js) emails
// sellers who started onboarding and stopped. "Started" is this stamp: the
// moment the account is created or an onboarding link is requested. Best
// effort, never in the way of the flow itself.
const stampStripeOnboardingTouched = sdk => {
  sdk.currentUser
    .updateProfile({ privateData: { stripeOnboardingTouchedAt: new Date().toISOString() } })
    .catch(() => null);
};

// Backward compatible wrapper function
export const createStripeAccount = params => (dispatch, getState, sdk) => {
  return dispatch(createStripeAccountThunk(params))
    .unwrap()
    .then(result => {
      stampStripeOnboardingTouched(sdk);
      return result;
    });
};

///////////////////////////
// Update Stripe Account //
///////////////////////////
const updateStripeAccountPayloadCreator = (params, { extra: sdk, rejectWithValue }) => {
  // This function is used for updating the bank account token
  // but could be expanded to other information as well.
  //
  // If the Stripe account has been created with account token,
  // you need to use account token also to update the account.
  // By default the account token will not be used.
  // See API reference for more information:
  // https://www.sharetribe.com/api-reference/?javascript#update-stripe-account
  return sdk.stripeAccount
    .update({ requestedCapabilities: ['card_payments', 'transfers'] }, { expand: true })
    .then(response => {
      const stripeAccount = response.data.data;
      return stripeAccount;
    })
    .catch(err => {
      const e = storableError(err);
      const stripeMessage =
        e.apiErrors && e.apiErrors.length > 0 && e.apiErrors[0].meta
          ? e.apiErrors[0].meta.stripeMessage
          : null;
      log.error(err, 'update-stripe-account-failed', { stripeMessage });
      return rejectWithValue(e);
    });
};
export const updateStripeAccountThunk = createAsyncThunk(
  'stripeConnectAccount/updateStripeAccount',
  updateStripeAccountPayloadCreator
);
// Backward compatible wrapper function
export const updateStripeAccount = params => dispatch => {
  return dispatch(updateStripeAccountThunk(params)).unwrap();
};

//////////////////////////
// Fetch Stripe Account //
//////////////////////////
const fetchStripeAccountPayloadCreator = (params, { extra: sdk, rejectWithValue }) => {
  return sdk.stripeAccount
    .fetch()
    .then(response => {
      const stripeAccount = response.data.data;
      return stripeAccount;
    })
    .catch(err => {
      const e = storableError(err);
      const stripeMessage =
        e.apiErrors && e.apiErrors.length > 0 && e.apiErrors[0].meta
          ? e.apiErrors[0].meta.stripeMessage
          : null;
      log.error(err, 'fetch-stripe-account-failed', { stripeMessage });
      return rejectWithValue(e);
    });
};
export const fetchStripeAccountThunk = createAsyncThunk(
  'stripeConnectAccount/fetchStripeAccount',
  fetchStripeAccountPayloadCreator
);
// Backward compatible wrapper function
export const fetchStripeAccount = params => dispatch => {
  return dispatch(fetchStripeAccountThunk(params)).unwrap();
};

////////////////////////////////
// Attach external bank account //
////////////////////////////////
// Attaches the seller's bank account to their connected account via the
// API *after* onboarding, using the compact IBAN we generated from the
// 11-digit kontonummer they entered on our form. Done post-onboarding
// (rather than prefilled at create) so Stripe never asks the seller to
// confirm the IBAN on the hosted page. Once the external account is on
// file Stripe flips `payouts_enabled` to true on its own — no further
// user-facing confirmation. The IBAN is tokenised fresh here because
// Stripe bank-account tokens are single-use and short-lived.
const attachExternalAccountPayloadCreator = (
  { compactIban, accountType, stripePublishableKey },
  { extra: sdk, getState, rejectWithValue }
) => {
  if (typeof window === 'undefined' || !window.Stripe) {
    throw new Error('Stripe must be loaded for attaching an external account');
  }
  if (!compactIban) {
    return rejectWithValue(storableError(new Error('No IBAN available to attach')));
  }
  const stripe = window.Stripe(stripePublishableKey);

  const currentUser = getState()?.user?.currentUser;
  const userPrefill = getStripePrefillFromUser(currentUser, accountType);
  // The account holder: the company's name for a company, the person's for
  // an individual.
  const person = userPrefill?.individual || {};
  const accountHolderName =
    userPrefill?.company?.name ||
    [person.first_name, person.last_name].filter(Boolean).join(' ') ||
    undefined;

  return stripe
    .createToken('bank_account', {
      country: 'NO',
      currency: 'nok',
      account_number: compactIban,
      account_holder_type: accountType === 'company' ? 'company' : 'individual',
      ...(accountHolderName ? { account_holder_name: accountHolderName } : {}),
    })
    .then(bankTokenResponse => {
      if (bankTokenResponse?.error) {
        const stripeErr = new Error(
          bankTokenResponse.error.message || 'Stripe bank token creation failed'
        );
        stripeErr.stripeError = bankTokenResponse.error;
        throw stripeErr;
      }
      const bankAccountToken = bankTokenResponse.token.id;
      return sdk.stripeAccount.update({ bankAccountToken }, { expand: true });
    })
    .then(response => response.data.data)
    .catch(err => {
      const e = storableError(err);
      if (err?.stripeError && !(e.apiErrors && e.apiErrors.length)) {
        e.apiErrors = [
          {
            status: 400,
            code: err.stripeError.code || 'stripe-token-create-failed',
            meta: { stripeMessage: err.stripeError.message },
          },
        ];
      }
      const stripeMessage =
        e.apiErrors && e.apiErrors.length > 0 && e.apiErrors[0].meta
          ? e.apiErrors[0].meta.stripeMessage
          : null;
      log.error(err, 'attach-external-account-failed', { stripeMessage });
      return rejectWithValue(e);
    });
};
export const attachExternalAccountThunk = createAsyncThunk(
  'stripeConnectAccount/attachExternalAccount',
  attachExternalAccountPayloadCreator
);
// Backward compatible wrapper function
export const attachExternalAccount = params => dispatch => {
  return dispatch(attachExternalAccountThunk(params)).unwrap();
};

/////////////////////////////////////
// Get Stripe Connect Account Link //
/////////////////////////////////////
const getStripeConnectAccountLinkPayloadCreator = (params, { extra: sdk, rejectWithValue }) => {
  const { failureURL, successURL, type } = params;

  // Read more from collection_options and verification updates from Stripe's Docs:
  // https://docs.stripe.com/connect/handle-verification-updates
  return sdk.stripeAccountLinks
    .create({
      failureURL,
      successURL,
      type,
      // NB: Stripe's accountLinks.create supports a `locale` param
      // (e.g. 'nb-NO' to force Norwegian), but Sharetribe's API rejects
      // it ("validation-disallowed-key"), so we can't set it here. The
      // hosted page falls back to the seller's browser language, which is
      // Norwegian for Norwegian users.
      collectionOptions: {
        fields: 'currently_due',
        future_requirements: 'include',
      },
    })
    .then(response => {
      // Return the account link
      return response.data.data.attributes.url;
    })
    .catch(err => {
      const e = storableError(err);
      const stripeMessage =
        e.apiErrors && e.apiErrors.length > 0 && e.apiErrors[0].meta
          ? e.apiErrors[0].meta.stripeMessage
          : null;
      log.error(err, 'get-stripe-account-link-failed', { stripeMessage });
      return rejectWithValue(e);
    });
};
export const getStripeConnectAccountLinkThunk = createAsyncThunk(
  'stripeConnectAccount/getStripeConnectAccountLink',
  getStripeConnectAccountLinkPayloadCreator
);
// Backward compatible wrapper function
export const getStripeConnectAccountLink = params => (dispatch, getState, sdk) => {
  return dispatch(getStripeConnectAccountLinkThunk(params))
    .unwrap()
    .then(result => {
      stampStripeOnboardingTouched(sdk);
      return result;
    });
};

// ================ Slice ================ //

const stripeConnectAccountSlice = createSlice({
  name: 'stripeConnectAccount',
  initialState: {
    createStripeAccountInProgress: false,
    createStripeAccountError: null,
    updateStripeAccountInProgress: false,
    updateStripeAccountError: null,
    fetchStripeAccountInProgress: false,
    fetchStripeAccountError: null,
    getAccountLinkInProgress: false,
    getAccountLinkError: null,
    stripeAccount: null,
    stripeAccountFetched: false,
  },
  reducers: {
    stripeAccountClearError: state => {
      return {
        createStripeAccountInProgress: false,
        createStripeAccountError: null,
        updateStripeAccountInProgress: false,
        updateStripeAccountError: null,
        fetchStripeAccountInProgress: false,
        fetchStripeAccountError: null,
        getAccountLinkInProgress: false,
        getAccountLinkError: null,
        stripeAccount: null,
        stripeAccountFetched: false,
      };
    },
    // Two things write this slice: `sdk.stripeAccount.fetch()`, which returns
    // the live Stripe payload including `requirements`, and the `stripeAccount`
    // relationship included with every currentUser fetch, which carries only
    // the account id — Sharetribe does not expand `stripeAccountData` on an
    // include. A plain assignment therefore let the second one wipe the first
    // on the next page load, which is why the verification banner appeared and
    // then vanished as you moved around the site. Keep the richer payload.
    updateStripeConnectAccount: (state, action) => {
      const incoming = action.payload;
      const hadData = !!state.stripeAccount?.attributes?.stripeAccountData;
      const bringsData = !!incoming?.attributes?.stripeAccountData;
      const isSameAccount = state.stripeAccount?.id?.uuid === incoming?.id?.uuid;

      if (hadData && isSameAccount && !bringsData) {
        return;
      }
      state.stripeAccount = incoming;
      state.stripeAccountFetched = true;
    },
  },
  extraReducers: builder => {
    builder
      // Create Stripe Account cases
      .addCase(createStripeAccountThunk.pending, state => {
        state.createStripeAccountError = null;
        state.createStripeAccountInProgress = true;
      })
      .addCase(createStripeAccountThunk.fulfilled, (state, action) => {
        state.createStripeAccountInProgress = false;
        state.stripeAccount = action.payload;
        state.stripeAccountFetched = true;
      })
      .addCase(createStripeAccountThunk.rejected, (state, action) => {
        console.error(action.payload);
        state.createStripeAccountError = action.payload;
        state.createStripeAccountInProgress = false;
      })
      // Update Stripe Account cases
      .addCase(updateStripeAccountThunk.pending, state => {
        state.updateStripeAccountError = null;
        state.updateStripeAccountInProgress = true;
      })
      .addCase(updateStripeAccountThunk.fulfilled, (state, action) => {
        state.updateStripeAccountInProgress = false;
        state.stripeAccount = action.payload;
        state.stripeAccountFetched = true;
      })
      .addCase(updateStripeAccountThunk.rejected, (state, action) => {
        console.error(action.payload);
        state.updateStripeAccountError = action.payload;
        state.updateStripeAccountInProgress = false;
      })
      // Fetch Stripe Account cases
      .addCase(fetchStripeAccountThunk.pending, state => {
        state.fetchStripeAccountError = null;
        state.fetchStripeAccountInProgress = true;
      })
      .addCase(fetchStripeAccountThunk.fulfilled, (state, action) => {
        state.fetchStripeAccountInProgress = false;
        state.stripeAccount = action.payload;
        state.stripeAccountFetched = true;
      })
      .addCase(fetchStripeAccountThunk.rejected, (state, action) => {
        console.error(action.payload);
        state.fetchStripeAccountError = action.payload;
        state.fetchStripeAccountInProgress = false;
      })
      // Get Account Link cases
      .addCase(getStripeConnectAccountLinkThunk.pending, state => {
        state.getAccountLinkError = null;
        state.getAccountLinkInProgress = true;
      })
      .addCase(getStripeConnectAccountLinkThunk.fulfilled, state => {
        state.getAccountLinkInProgress = false;
      })
      .addCase(getStripeConnectAccountLinkThunk.rejected, (state, action) => {
        console.error(action.payload);
        state.getAccountLinkInProgress = false;
        state.getAccountLinkError = action.payload;
      });
  },
});

// Export the action creators
export const {
  stripeAccountClearError,
  updateStripeConnectAccount,
} = stripeConnectAccountSlice.actions;

export default stripeConnectAccountSlice.reducer;
